<template>
  <div class="help-root">
    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">event</span>
      <p>
        Administra la agenda de horarios de un médico: consulta por fecha,
        generación automática de turnos, habilitación/deshabilitación de
        días y edición de cada bloque horario.
      </p>
    </div>

    <!-- Datos del médico -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">badge</span>
        Datos del médico
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem">
        En la parte superior se muestra información de solo lectura del
        médico (nombre, documento, especialidad y número profesional).
        Los interruptores <strong>Agendar sábado</strong> y
        <strong>Agendar domingo</strong> definen si esos días se incluyen
        al generar la agenda automáticamente; el cambio se guarda de
        inmediato al activarlos o desactivarlos.
      </p>
    </section>

    <!-- Búsqueda -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">search</span>
        Búsqueda por fecha
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in busquedas" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Estados -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">info</span>
        Estados de la agenda
      </h3>
      <div class="help-estados">
        <span class="help-estado-badge" style="background:#d4edda;color:#155724">Habilitado</span>
        <span class="help-estado-badge" style="background:#f8d7da;color:#721c24">Deshabilitado</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-top:0.5rem">
        Cada fila tiene un interruptor de estado individual. Un bloque
        <strong>Habilitado</strong> está disponible para asignar citas; uno
        <strong>Deshabilitado</strong> se conserva pero no puede usarse para
        nuevas atenciones. El botón de la barra de herramientas permite
        habilitar o deshabilitar <em>todos</em> los bloques de la fecha
        seleccionada al mismo tiempo.
      </p>
    </section>

    <!-- Acciones -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Generación automática -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">auto_awesome</span>
        Generación automática de agenda
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="g in generacion" :key="g.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: g.color }">{{ g.icon }}</span>
          <div>
            <p class="help-card-title">{{ g.titulo }}</p>
            <p class="help-card-desc">{{ g.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Habilitar/Deshabilitar día -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">event_busy</span>
        Habilitar / deshabilitar un día completo
      </h3>
      <p class="help-card-desc" style="padding:0 0.25rem">
        Selecciona primero una <strong>Fecha</strong> en el filtro de
        búsqueda. El botón cambia entre "Habilitar" y "Deshabilitar" según
        el estado actual de los bloques de ese día. Es recomendable elegir
        un <strong>motivo</strong> de la lista (enfermedad, vacaciones,
        capacitación, etc.) antes de deshabilitar, ya que queda registrado
        como observación del cambio.
      </p>
    </section>

    <!-- Tip -->
    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        Al editar un bloque horario existente, los campos
        <strong>Fecha</strong>, <strong>Hora desde</strong> y
        <strong>Hora hasta</strong> no son editables; solo puedes modificar
        el <strong>Estado</strong> y la <strong>Observación</strong>. El
        selector de <strong>Motivo</strong> es opcional y, al elegir uno,
        se concatena automáticamente al texto de la observación. Usa
        <strong>Día</strong>, <strong>Mes</strong> o <strong>Año</strong>
        para generar los turnos automáticamente según la configuración de
        sábados y domingos del médico.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: "AyudaMedicoAgenda",
  data() {
    return {
      busquedas: [
        {
          icon: "calendar_today",
          titulo: "Por fecha",
          desc: "Selecciona una fecha en el filtro y pulsa la lupa para ver únicamente los bloques de agenda de ese día.",
        },
        {
          icon: "list_alt",
          titulo: "Todos los registros",
          desc: "El botón de recarga en la tabla muestra todos los bloques de agenda registrados para el médico.",
        },
      ],
      acciones: [
        {
          icon: "add_box",
          color: "#198754",
          titulo: "Nuevo",
          desc: "Abre el formulario para crear un nuevo bloque de horario (fecha, hora desde, hora hasta y observación).",
        },
        {
          icon: "edit",
          color: "#0059cc",
          titulo: "Editar",
          desc: "Permite cambiar el estado (Habilitado/Deshabilitado) y la observación de un bloque existente.",
        },
        {
          icon: "download",
          color: "#fd7e14",
          titulo: "Exportar Excel",
          desc: "Descarga en Excel los bloques de agenda mostrados actualmente en la tabla.",
        },
        {
          icon: "refresh",
          color: "#6c757d",
          titulo: "Recargar",
          desc: "Recarga todos los bloques de agenda del médico, eliminando el filtro de fecha aplicado.",
        },
      ],
      generacion: [
        {
          icon: "event",
          color: "#dc3545",
          titulo: "Día",
          desc: "Genera los bloques de horario únicamente para la fecha seleccionada en el filtro.",
        },
        {
          icon: "date_range",
          color: "#dc3545",
          titulo: "Mes",
          desc: "Genera los bloques de horario para todo el mes en curso, respetando si el médico atiende sábados y/o domingos.",
        },
        {
          icon: "calendar_month",
          color: "#dc3545",
          titulo: "Año",
          desc: "Genera los bloques de horario para todo el año en curso, respetando la configuración de sábados y domingos.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>